/**
 * Possible results of a LimeSurvey equation question (type "*"), read from its
 * expression text — e.g. {if(A.NAOK == "0" or B.NAOK == "0", "0", "1")} → ["0", "1"].
 *
 * Only literal results are understood: plain strings/numbers and nested
 * if(cond, then, else). Anything else (arithmetic, concatenation, a field
 * reference, text around the {...}) makes the result `complete: false`, and the
 * caller lets the user add the missing codes by hand.
 *
 * Returns { values: string[], complete: boolean, canBeEmpty: boolean }.
 */
export function extractEquationOutputs(rawText) {
  const text = decodeHtml(rawText || '').trim();
  if (!text) return { values: [], complete: false, canBeEmpty: true };

  // No expression at all: the equation always returns this constant text.
  if (!text.includes('{')) return { values: [text], complete: true, canBeEmpty: false };

  // Exactly one {...} block and nothing around it; otherwise the stored value is
  // a concatenation we can't enumerate.
  if (!(text.startsWith('{') && findClosing(text, 0, '{', '}') === text.length - 1)) {
    return { values: [], complete: false, canBeEmpty: false };
  }

  const result = outputsOf(text.slice(1, -1));
  const values = [...new Set(result.values.filter(v => v !== ''))];
  return {
    values,
    complete: result.complete,
    canBeEmpty: result.canBeEmpty || result.values.includes(''),
  };
}

function outputsOf(rawExpr) {
  const expr = stripOuterParens(rawExpr.trim());

  const lit = literal(expr);
  if (lit !== null) return { values: [lit], complete: true, canBeEmpty: false };

  const ifMatch = expr.match(/^if\s*\(/i);
  if (ifMatch) {
    const open = ifMatch[0].length - 1;
    if (findClosing(expr, open, '(', ')') === expr.length - 1) {
      const args = splitArgs(expr.slice(open + 1, -1));
      if (args.length === 3 || args.length === 2) {
        const branches = args.slice(1).map(outputsOf);
        return {
          values: branches.flatMap(b => b.values),
          complete: branches.every(b => b.complete),
          // if(cond, x) returns "" when cond is false.
          canBeEmpty: args.length === 2 || branches.some(b => b.canBeEmpty),
        };
      }
    }
  }

  return { values: [], complete: false, canBeEmpty: false };
}

function literal(expr) {
  const quoted = expr.match(/^"((?:[^"\\]|\\.)*)"$/) || expr.match(/^'((?:[^'\\]|\\.)*)'$/);
  if (quoted) return quoted[1].replace(/\\(.)/g, '$1').trim();
  if (/^-?\d+(\.\d+)?$/.test(expr)) return expr;
  return null;
}

// Index of the bracket closing the one at `start`, skipping quoted strings; -1 if none.
function findClosing(s, start, open, close) {
  let depth = 0;
  let quote = null;
  for (let i = start; i < s.length; i++) {
    const ch = s[i];
    if (quote) {
      if (ch === '\\') i++;
      else if (ch === quote) quote = null;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === open) {
      depth++;
    } else if (ch === close) {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function stripOuterParens(expr) {
  while (expr.startsWith('(') && findClosing(expr, 0, '(', ')') === expr.length - 1) {
    expr = expr.slice(1, -1).trim();
  }
  return expr;
}

// Split function arguments on top-level commas.
function splitArgs(s) {
  const args = [];
  let depth = 0;
  let quote = null;
  let last = 0;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (quote) {
      if (ch === '\\') i++;
      else if (ch === quote) quote = null;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === '(') {
      depth++;
    } else if (ch === ')') {
      depth--;
    } else if (ch === ',' && depth === 0) {
      args.push(s.slice(last, i));
      last = i + 1;
    }
  }
  args.push(s.slice(last));
  return args;
}

// The LimeSurvey editor stores question text as HTML (<p>, &quot;, &nbsp;...).
function decodeHtml(s) {
  const text = new DOMParser().parseFromString(String(s), 'text/html').documentElement.textContent || '';
  return text.replace(/ /g, ' ');
}
